import * as React from "react";
import { useState } from "react";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { usePromptFieldAdminQEMutation } from "./fieldAdminApi";
import { AMBER } from "../../Global/colors";
import { FAGhostButton, FAPrimaryButton } from "./FAControls";
import { FONT_FAMILY, BAD_RED_TEXT, stripErrorPrefix } from "./designTokens";
import { FA_DIALOG_PAPER_SX } from "./FALayout";

// The "prompt the QE" dialog — its own mutation and sent/error state.

export function PromptModal({ job, qeName, onClose }) {
  const [message, setMessage] = useState("");
  const [promptQE, { isLoading, error }] = usePromptFieldAdminQEMutation();
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    const result = await promptQE({ job_card_reference_id: job.reference_id, message });
    // A failed prompt used to leave the dialog sitting there unchanged, which reads as
    // "nothing happened" rather than "the nudge did not send".
    if (!result.error) setSent(true);
  };

  return (
    <Dialog open onClose={onClose} fullWidth sx={[FA_DIALOG_PAPER_SX, { "& .MuiDialog-paper": { maxWidth: "420px" } }]}>
      {sent ? (
        <>
          <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, color: "#fff", pb: "6px" }}>Prompt sent</DialogTitle>
          <DialogContent sx={{ pb: "8px" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.84rem", lineHeight: 1.5 }}>
              The nudge is recorded against this install. The QE retains all authority — nothing on your side has changed.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: "14px 20px" }}>
            <Box sx={{ flex: 1 }} />
            <FAPrimaryButton onClick={onClose}>Done</FAPrimaryButton>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "1.02rem", fontWeight: 700, color: "#fff" }}>
            <SendOutlinedIcon sx={{ color: AMBER, fontSize: "1.05rem" }} />
            Prompt {qeName || "the QE"}?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.84rem", lineHeight: 1.5 }}>
              Sends an in-app nudge with the install reference{job.address ? ` for ${job.address}` : ""}. The QE retains all authority — this is a reminder, not an instruction.
            </Typography>
            <Box
              component="textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional message (visible to the QE)…"
              rows={3}
              sx={{
                width: "100%", mt: "14px", padding: "10px 12px", backgroundColor: "#0D1117",
                border: "1px solid rgba(255,255,255,0.08)", borderRadius: "8px", color: "#fff",
                fontFamily: FONT_FAMILY, fontSize: "0.84rem", resize: "vertical", boxSizing: "border-box",
                outline: "none",
                "&:focus": { borderColor: "rgba(253,185,64,0.4)" },
                "&::placeholder": { color: "rgba(255,255,255,0.3)" },
              }}
            />
            {error ? (
              <Typography sx={{ color: BAD_RED_TEXT, fontSize: "0.8rem", mt: "10px" }}>
                {stripErrorPrefix(error?.data?.error_message) || "The prompt could not be sent. Try again."}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ p: "14px 20px" }}>
            <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
            <Box sx={{ flex: 1 }} />
            <FAPrimaryButton onClick={handleSend} disabled={isLoading}>
              {isLoading ? "Sending…" : "Send prompt"}
            </FAPrimaryButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
